import {
  OktonErrorCard,
  OktonMetricCard,
  OktonDateFilter,
  OktonPageHeader,
} from '@okton/platform-ui';
import { Grid, Stack } from '@chakra-ui/react';
import { AlertTriangle, CheckCircle2, Clock, Landmark } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useDashboardPainel } from '../../services/hooks/useDashboardPainel';
import {
  Agrupamento,
  FiltrosDashboard,
} from '../../services/hooks/useDashboard';
import formatValue from '../../utils/formatValue';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import { EstadoConciliacao } from '../conciliacao/estadoNavegacao';
import AttentionPanel, { TipoPendencia } from './components/AttentionPanel';
import ContasTable from './components/ContasTable';
import EntradasSaidasChart from './components/EntradasSaidasChart';
import EvolucaoConciliacaoChart from './components/EvolucaoConciliacaoChart';
import FiltersBar, { ValoresFiltro } from './components/FiltersBar';
import MovimentacoesChart from './components/MovimentacoesChart';
import SaldoChart from './components/SaldoChart';
import SituacaoChart, { Situacao } from './components/SituacaoChart';
import UltimasConciliacoesTable from './components/UltimasConciliacoesTable';
import { formatPercentual, intervaloDoPeriodo } from './formatacao';

const FILTROS_VAZIOS: ValoresFiltro = {
  conta: '',
  moeda: '',
  centrocusto: '',
  departamento: '',
};

/**
 * Montado com os mesmos componentes das telas do Analytics - InfoCard nos
 * indicadores, Chart nos gráficos (com a ChartInfo ao lado, como no
 * DashboardCEO) e OktonDataGrid nas tabelas -, para os dois produtos se
 * parecerem.
 */
const Dashboard = () => {
  const history = useHistory();

  const [periodo, setPeriodo] = useState<FiltroData>(setaFiltroAnual());
  const [valores, setValores] = useState<ValoresFiltro>(FILTROS_VAZIOS);

  const filtros: FiltrosDashboard = useMemo(
    () => ({ ...valores, dataini: periodo[1], datafim: periodo[2] }),
    [valores, periodo],
  );

  const {
    indicadores,
    evolucao,
    contas,
    pendencias,
    movimentacoes,
    ultimasConciliacoes: ultimas,
    contasCadastro: cadastro,
  } = useDashboardPainel(filtros);
  const contasCadastro = cadastro.data ?? [];

  const dados = indicadores.data;
  const listaContas = contas.data ?? [];

  // A Conta do filtro, se houver; senão a que concentra o que o usuário quer
  // ver - a lista de contas já vem ordenada por pendências.
  const contaDestino = (conta?: string | null) =>
    valores.conta || conta || listaContas[0]?.id_cts;

  const abrirConciliacao = (estado: EstadoConciliacao) =>
    history.push('/conciliacao', { periodo, ...estado });

  // Conciliados filtrados nas divergências, na Conta que mais tem.
  const abrirDivergencias = () =>
    abrirConciliacao({
      conta: contaDestino(pendencias.data?.divergencias.conta),
      tab: 'done',
      divergentes: true,
    });

  const abrirPendencia = (tipo: TipoPendencia, conta: string | null) => {
    if (tipo === 'contassematualizacao') {
      history.push('/extrato-bancario');
      return;
    }
    if (tipo === 'divergencias') {
      abrirDivergencias();
      return;
    }
    abrirConciliacao({ conta: contaDestino(conta), tab: 'pending' });
  };

  const abrirSituacao = (situacao: Situacao) => {
    if (situacao === 'divergentes') {
      abrirDivergencias();
      return;
    }
    abrirConciliacao({
      conta: contaDestino(),
      tab: situacao === 'pendentes' ? 'pending' : 'done',
    });
  };

  const selecionarPeriodo = (inicio: string, agrupamento: Agrupamento) => {
    const [dataIni, dataFim] = intervaloDoPeriodo(inicio, agrupamento);
    setPeriodo(['Personalizado', dataIni, dataFim]);
  };

  const variacao = dados?.variacaosaldo ? Number(dados.variacaosaldo) : null;
  const percentualConciliado =
    dados && dados.extratototal > 0
      ? (dados.extratoconciliado * 100) / dados.extratototal
      : 0;

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="GERAL"
        title="Dashboard Conciliação"
        subtitle="Visão geral das contas bancárias e da conciliação"
        actions={
          <OktonDateFilter
            value={{ preset: periodo[0], start: periodo[1], end: periodo[2] }}
            onChange={next => setPeriodo([next.preset, next.start, next.end])}
          />
        }
      />

      {cadastro.isError ? (
        <OktonErrorCard
          description={
            cadastro.error?.message || 'Não foi possível carregar as contas.'
          }
          onRetry={cadastro.refetch}
        />
      ) : (
        <FiltersBar
          contas={contasCadastro}
          valores={valores}
          onChange={setValores}
        />
      )}

      <Stack spacing="6" mt="6" mb="10">
        {/* ─── Indicadores ─── */}
        {/* Dois por linha, como no Financial do Analytics: o InfoCard divide o
            card entre valor e descrição, e com quatro lado a lado abaixo de
            1536px o valor invade a descrição. */}
        {indicadores.isError ? (
          <OktonErrorCard
            description={
              indicadores.error instanceof Error && indicadores.error.message
                ? indicadores.error.message
                : 'Tente novamente. Se o problema persistir, contate o suporte.'
            }
            onRetry={indicadores.refetch}
          />
        ) : (
          <Grid
            templateColumns={[
              '1fr',
              'repeat(2, 1fr)',
              null,
              null,
              null,
              'repeat(4, 1fr)',
            ]}
            gap="5"
          >
            <OktonMetricCard
              label="Saldo Bancário"
              value={formatValue(dados?.saldototal ?? 0)}
              icon={Landmark}
              accentColor="blue.500"
              isLoading={indicadores.isLoading}
              description={
                variacao === null
                  ? 'Sem saldo no mês anterior'
                  : `${variacao >= 0 ? '▲' : '▼'} ${formatPercentual(
                      Math.abs(variacao),
                    )} vs mês anterior`
              }
            />
            <OktonMetricCard
              label="A conciliar"
              value={(dados?.extratopendente ?? 0).toLocaleString('pt-BR')}
              icon={Clock}
              accentColor="orange.500"
              isLoading={indicadores.isLoading}
              description={formatValue(dados?.valorpendente ?? 0)}
              onClick={() =>
                abrirConciliacao({ conta: contaDestino(), tab: 'pending' })
              }
            />
            <OktonMetricCard
              label="Conciliados"
              value={`${(dados?.extratoconciliado ?? 0).toLocaleString(
                'pt-BR',
              )} de ${(dados?.extratototal ?? 0).toLocaleString('pt-BR')}`}
              icon={CheckCircle2}
              accentColor="green.500"
              isLoading={indicadores.isLoading}
              description={`${formatPercentual(
                percentualConciliado,
              )} do extrato`}
              onClick={() =>
                abrirConciliacao({ conta: contaDestino(), tab: 'done' })
              }
            />
            <OktonMetricCard
              label="Divergências"
              value={(dados?.divergencias ?? 0).toLocaleString('pt-BR')}
              icon={AlertTriangle}
              accentColor="red.500"
              isLoading={indicadores.isLoading}
              description={
                dados?.divergencias
                  ? `${formatValue(dados.valordivergente)} de diferença`
                  : 'Nenhuma diferença'
              }
              onClick={abrirDivergencias}
            />
          </Grid>
        )}

        {/* ─── Evolução e situação ─── */}
        <Grid templateColumns={['1fr', null, null, '3fr 2fr']} gap="6">
          <EvolucaoConciliacaoChart
            dados={evolucao.data}
            atualizando={evolucao.isPreviousData}
            isLoading={evolucao.isLoading}
            isError={evolucao.isError}
            error={evolucao.error}
            onRetry={evolucao.refetch}
          />
          <SituacaoChart
            dados={dados}
            atualizando={indicadores.isPreviousData}
            isLoading={indicadores.isLoading}
            isError={indicadores.isError}
            error={indicadores.error}
            onRetry={indicadores.refetch}
            onAbrir={abrirSituacao}
          />
        </Grid>

        {/* ─── Saldo e pendências ─── */}
        <Grid templateColumns={['1fr', null, null, '3fr 2fr']} gap="6">
          <SaldoChart
            filtros={filtros}
            contas={contasCadastro}
            evolucaoGeral={evolucao}
          />
          <AttentionPanel
            dados={pendencias.data}
            isLoading={pendencias.isLoading}
            isError={pendencias.isError}
            error={pendencias.error}
            onRetry={pendencias.refetch}
            onAbrir={abrirPendencia}
            onVerTodas={() =>
              abrirConciliacao({ conta: contaDestino(), tab: 'pending' })
            }
          />
        </Grid>

        {/* ─── Contas bancárias ─── */}
        <ContasTable
          contas={listaContas}
          isLoading={contas.isLoading}
          isError={contas.isError}
          error={contas.error}
          onRetry={contas.refetch}
          onAbrir={conta => abrirConciliacao({ conta })}
        />

        {/* ─── Entradas e saídas ─── */}
        <Grid templateColumns={['1fr', null, null, '3fr 2fr']} gap="6">
          <EntradasSaidasChart
            dados={evolucao.data}
            atualizando={evolucao.isPreviousData}
            isLoading={evolucao.isLoading}
            isError={evolucao.isError}
            error={evolucao.error}
            onRetry={evolucao.refetch}
            onSelecionarPeriodo={selecionarPeriodo}
          />
          <MovimentacoesChart
            movimentacoes={movimentacoes.data ?? []}
            atualizando={movimentacoes.isPreviousData}
            isLoading={movimentacoes.isLoading}
            isError={movimentacoes.isError}
            error={movimentacoes.error}
            onRetry={movimentacoes.refetch}
          />
        </Grid>

        {/* ─── Atividade recente ─── */}
        <UltimasConciliacoesTable
          conciliacoes={ultimas.data ?? []}
          isLoading={ultimas.isLoading}
          isError={ultimas.isError}
          error={ultimas.error}
          onRetry={ultimas.refetch}
          onVerTodas={() =>
            abrirConciliacao({ conta: contaDestino(), tab: 'done' })
          }
        />
      </Stack>
    </BankAppPage>
  );
};

export default Dashboard;
